import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, BadgeCheck, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { MEMBERSHIP_BENEFITS, PERSONAS, waLink } from "@/data/content";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { FadeUp } from "@/components/Reveal";

export const CURRENCIES = [
  { code: "GBP", symbol: "£" },
  { code: "USD", symbol: "$" },
  { code: "EUR", symbol: "€" },
  { code: "NGN", symbol: "₦" },
];

export const memberApi = () => {
  const token = localStorage.getItem("jawa_member_token");
  if (token) api.defaults.headers.common.Authorization = `Bearer ${token}`;
  else delete api.defaults.headers.common.Authorization;
  return api;
};

export const MemberAuthForm = ({ onAuthed, compact = false }) => {
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post(`/members/${mode}`, form);
      localStorage.setItem("jawa_member_token", res.data.token);
      toast.success(mode === "register" ? "Welcome to JÀWÀ." : "Welcome back.");
      onAuthed(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full border border-jawa-espresso/20 bg-white px-4 py-3 text-sm text-jawa-espresso outline-none transition-colors focus:border-jawa-turquoise";

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleSignIn = () => {
    const redirectUrl = window.location.origin + "/members";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <form onSubmit={submit} className={compact ? "space-y-3" : "space-y-4"} data-testid="member-auth-form">
      <button
        type="button"
        onClick={googleSignIn}
        data-testid="google-signin-button"
        className="flex w-full items-center justify-center gap-3 border border-jawa-espresso/25 bg-white px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-espresso transition-colors hover:border-jawa-turquoise hover:text-jawa-turquoise"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
          <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.3h6.5c-.1 1.1-.8 2.7-2.4 3.8l-.02.15 3.5 2.7.24.03c2.2-2 3.5-5 3.5-8.7z"/>
          <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.8-2.9c-1 .7-2.4 1.2-4.1 1.2-3.1 0-5.8-2.1-6.8-5l-.14.01-3.1 2.4-.04.11C3.9 21.3 7.6 24 12 24z"/>
          <path fill="#FBBC05" d="M5.2 14.4c-.24-.7-.38-1.5-.38-2.4s.14-1.7.36-2.4l-.01-.16-3.14-2.4-.1.05C1.3 8.9 1 10.4 1 12s.3 3.1.93 4.5l3.27-2.1z"/>
          <path fill="#EA4335" d="M12 4.6c2.3 0 3.8 1 4.7 1.8l3.4-3.3C18.9 1.1 15.2 0 12 0 7.6 0 3.9 2.7 1.93 6.9l3.26 2.5c1-2.9 3.7-4.8 6.8-4.8z"/>
        </svg>
        Continue with Google
      </button>
      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-jawa-espresso/15" />
        <span className="text-[10px] uppercase tracking-[0.25em] text-jawa-espresso/40">or with email</span>
        <span className="h-px flex-1 bg-jawa-espresso/15" />
      </div>
      {mode === "register" && (
        <input
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Full name"
          data-testid="member-name"
          className={inputCls}
        />
      )}
      <input
        type="email"
        required
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        placeholder="Email address"
        data-testid="member-email"
        className={inputCls}
      />
      <input
        type="password"
        required
        minLength={8}
        value={form.password}
        onChange={(e) => setForm({ ...form, password: e.target.value })}
        placeholder="Password (min. 8 characters)"
        data-testid="member-password"
        className={inputCls}
      />
      <button
        type="submit"
        disabled={busy}
        data-testid="member-auth-submit"
        className="flex w-full items-center justify-center gap-2 bg-jawa-coral px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors hover:bg-jawa-coral/90 disabled:opacity-60"
      >
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        {mode === "register" ? "Create my account" : "Sign in"}
      </button>
      <button
        type="button"
        onClick={() => setMode(mode === "register" ? "login" : "register")}
        data-testid="member-auth-toggle"
        className="w-full text-center text-xs uppercase tracking-[0.2em] text-jawa-espresso/50 transition-colors hover:text-jawa-turquoise"
      >
        {mode === "register" ? "Already a member? Sign in" : "New here? Create an account"}
      </button>
    </form>
  );
};

export default function MembershipPage() {
  const { audience } = useParams();
  const persona = PERSONAS[audience];
  const [plans, setPlans] = useState([]);
  const { currency, setCurrency } = useCurrency();
  const [period, setPeriod] = useState("monthly");
  const [member, setMember] = useState(null);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/plans").then((res) => setPlans(res.data)).catch(() => {});
    const token = localStorage.getItem("jawa_member_token");
    if (token) {
      memberApi().get("/members/me").then((res) => setMember(res.data)).catch(() => {
        localStorage.removeItem("jawa_member_token");
      });
    }
  }, []);

  const plan = useMemo(
    () => plans.find((p) => p.audience === audience && p.period === period),
    [plans, audience, period]
  );
  const symbol = CURRENCIES.find((c) => c.code === currency)?.symbol || "";
  const price = plan?.prices?.[currency];
  const active = member?.memberships?.find(
    (m) => m.audience === audience && m.status === "active" && new Date(m.end_date) > new Date()
  );

  const requestMembership = async () => {
    setBusy(true);
    try {
      const res = await memberApi().post("/memberships", { audience, period, currency });
      setPending(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const payOnline = async () => {
    setBusy(true);
    try {
      const res = await api.post("/paystack/initialize", {
        kind: "membership",
        audience,
        period,
        email: member.email,
        name: member.name,
      });
      window.location.href = res.data.authorization_url;
    } catch (err) {
      const msg = formatApiError(err);
      if (msg.toLowerCase().includes("being configured")) {
        toast.info("Online card payment is being switched on — the WhatsApp transfer below works today.", { duration: 6000 });
      } else {
        toast.error(msg);
      }
      setBusy(false);
    }
  };

  if (!persona) {
    return (
      <div className="min-h-screen bg-jawa-ivory">
        <Nav />
        <div className="pt-40 pb-24 text-center font-serif text-3xl text-jawa-espresso">Membership not found</div>
        <Footer />
      </div>
    );
  }

  const waText = pending
    ? `Hello JÀWÀ — I would like to request payment details for the ${persona.title} Membership (${period}, ${symbol}${price} ${currency}).\nName: ${member?.name}\nEmail: ${member?.email}\nReference: ${pending.reference}`
    : "";

  return (
    <div className="bg-jawa-ivory" data-testid={`membership-page-${audience}`}>
      <Nav />
      <main className="mx-auto max-w-6xl px-6 pb-24 pt-32 lg:px-10 lg:pt-40">
        <FadeUp>
          <p className="text-xs uppercase tracking-[0.35em] text-jawa-turquoise">Membership · {persona.title}</p>
          <h1 className="mt-3 font-serif text-4xl font-medium tracking-tight text-jawa-espresso sm:text-5xl">
            JÀWÀ {persona.title.replace("The ", "")} Membership
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-jawa-espresso/70">
            Unlock the members-only {persona.guideCta} — a complete, audience-specific A–Z with practical
            checklists, directories and resources, kept current by our editors.
          </p>
        </FadeUp>

        <FadeUp className="mt-12">
          <div className="border border-jawa-turquoise/40 bg-white p-6 lg:p-8" data-testid="membership-benefits">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-jawa-espresso">What's included</p>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {MEMBERSHIP_BENEFITS[audience].map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-jawa-espresso/75">
                  <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-jawa-turquoise" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </FadeUp>

        <div className="mt-12 grid gap-8 lg:grid-cols-2">
          <FadeUp>
            <div className="h-full border border-jawa-stone bg-white p-6 lg:p-8" data-testid="membership-pricing">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-jawa-espresso">Choose your plan</p>
                <div className="flex gap-1" data-testid="currency-selector">
                  {CURRENCIES.map((c) => (
                    <button
                      key={c.code}
                      onClick={() => setCurrency(c.code)}
                      data-testid={`currency-${c.code.toLowerCase()}`}
                      className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                        currency === c.code ? "bg-jawa-espresso text-jawa-ivory" : "border border-jawa-espresso/20 text-jawa-espresso/60 hover:text-jawa-espresso"
                      }`}
                    >
                      {c.code}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                {["monthly", "annual"].map((per) => {
                  const pl = plans.find((p) => p.audience === audience && p.period === per);
                  const pr = pl?.prices?.[currency];
                  return (
                    <button
                      key={per}
                      onClick={() => setPeriod(per)}
                      data-testid={`plan-${per}`}
                      className={`border p-5 text-left transition-colors ${
                        period === per ? "border-jawa-coral bg-jawa-coral/5" : "border-jawa-stone hover:border-jawa-espresso/40"
                      }`}
                    >
                      <p className="text-[10px] uppercase tracking-[0.25em] text-jawa-espresso/50">{per}</p>
                      <p className="mt-2 font-serif text-3xl font-medium text-jawa-espresso">
                        {pr != null ? `${symbol}${pr.toLocaleString()}` : "—"}
                      </p>
                      {per === "annual" && (
                        <p className="mt-1 text-[10px] uppercase tracking-[0.15em] text-jawa-turquoise">Best value</p>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="mt-4 text-xs leading-relaxed text-jawa-espresso/50">
                Suggested local launch prices — not live exchange-rate conversions. Memberships do not renew
                automatically; request a renewal from your member area before expiry.
              </p>

              {active ? (
                <div className="mt-6 border border-jawa-turquoise/50 bg-jawa-turquoise/10 p-5" data-testid="membership-active-panel">
                  <p className="flex items-center gap-2 text-sm font-semibold text-jawa-espresso">
                    <ShieldCheck className="h-4 w-4 text-jawa-turquoise" />
                    Active until {new Date(active.end_date).toLocaleDateString()}
                  </p>
                  <Link
                    to={`/members/guides/${audience}`}
                    data-testid="membership-open-guide"
                    className="mt-4 inline-flex items-center gap-1.5 bg-jawa-espresso px-5 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-ivory hover:bg-jawa-terracotta"
                  >
                    Open your A–Z collection <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              ) : pending ? (
                <div className="mt-6 border border-jawa-gold/50 bg-jawa-gold/10 p-5" data-testid="membership-pending-panel">
                  <p className="text-sm font-semibold text-jawa-espresso">
                    Reference: <span className="font-mono">{pending.reference}</span>
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-jawa-espresso/70">
                    Your membership is reserved. Tap below — WhatsApp opens with your details prefilled — and we'll
                    reply with the bank details for your currency. We never publish bank details on the website.
                    Once your transfer is verified, your membership activates and you'll receive a confirmation email.
                  </p>
                  <button
                    onClick={payOnline}
                    disabled={busy}
                    data-testid="membership-paystack-button"
                    className="mt-4 inline-flex w-full items-center justify-center gap-1.5 bg-jawa-coral px-5 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-jawa-espresso disabled:opacity-60"
                  >
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                    Pay online now — card or bank (Paystack) <ArrowUpRight className="h-4 w-4" />
                  </button>
                  <p className="mt-2 text-[11px] leading-relaxed text-jawa-espresso/50" data-testid="membership-charge-note">
                    Online payment is charged in naira (₦{plan?.prices?.NGN?.toLocaleString()}) at the agreed naira plan
                    price via Paystack — the displayed {currency} price is the agreed {currency} launch price.
                  </p>
                  <div className="mt-4 flex items-center gap-3">
                    <span className="h-px flex-1 bg-jawa-espresso/15" />
                    <span className="text-[10px] uppercase tracking-[0.25em] text-jawa-espresso/40">or bank transfer</span>
                    <span className="h-px flex-1 bg-jawa-espresso/15" />
                  </div>
                  <a
                    href={waLink(waText)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="membership-whatsapp-request"
                    className="mt-4 inline-flex items-center gap-1.5 bg-jawa-turquoise px-5 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-nearblack hover:bg-jawa-turquoise/90"
                  >
                    Request Payment Details on WhatsApp <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              ) : member ? (
                <button
                  onClick={requestMembership}
                  disabled={busy || price == null}
                  data-testid="membership-request-button"
                  className="mt-6 flex w-full items-center justify-center gap-2 bg-jawa-coral px-6 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors hover:bg-jawa-coral/90 disabled:opacity-60"
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  Continue as {member.name.split(" ")[0]} — {symbol}{price?.toLocaleString()} {currency}/{period}
                </button>
              ) : (
                <div className="mt-6">
                  <p className="mb-3 text-xs uppercase tracking-[0.2em] text-jawa-espresso/50">
                    Register to reserve your membership
                  </p>
                  <MemberAuthForm compact onAuthed={(m) => setMember({ ...m, memberships: [] })} />
                </div>
              )}
            </div>
          </FadeUp>

          <FadeUp delay={0.08}>
            <div className="relative h-full min-h-[320px] overflow-hidden">
              <img src={persona.image} alt={persona.title} className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-jawa-nearblack/85 via-transparent to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-7">
                <p className="text-[10px] uppercase tracking-[0.3em] text-jawa-gold">Built for</p>
                <p className="mt-1 font-serif text-2xl text-jawa-ivory">
                  People like {persona.name} — {persona.meta}
                </p>
                <Link
                  to={`/journeys/${audience}`}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-turquoise hover:text-jawa-ivory"
                >
                  Read {persona.name}'s journey <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </FadeUp>
        </div>
      </main>
      <Footer />
    </div>
  );
}
